import { isUsVersion } from "config/constants";
import { IMPERIAL_LANGUAGE } from "./units";

// en-US is not just a spelling variant: it switches the whole app to Imperial. So it must
// never be turned on by accident - a US visitor of the German site would silently get feet
// and inches (see the risk in 5.1 of US-IMPERIAL-TERV.md).
//
// Two things may select it: the US site (where it is the only language) and an explicit
// choice. The browser's own language is not enough, and neither is the language detector's
// cache, which cannot tell a detected value from a chosen one - hence the separate key here.

export const LANGUAGE_CHOICE_KEY = "editor-language";

export function getLanguageChoice(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_CHOICE_KEY);
  } catch {
    // private mode / blocked site data: no stored choice, the default applies
    return null;
  }
}

// call this wherever the person picks a language, before i18n.changeLanguage
export function rememberLanguageChoice(lng: string) {
  try {
    localStorage.setItem(LANGUAGE_CHOICE_KEY, lng);
  } catch {
    // the choice still applies for this session, it just will not be remembered
  }
}

// what the app may actually start up with, given what the detector came back with
export function normalizeDetectedLanguage(lng: string | undefined): string | undefined {
  // the US site has en-US only, whatever the browser says or was picked before
  if (isUsVersion()) {
    return IMPERIAL_LANGUAGE;
  }
  if (lng?.toLowerCase() !== IMPERIAL_LANGUAGE.toLowerCase()) {
    return lng;
  }
  return getLanguageChoice() === IMPERIAL_LANGUAGE ? IMPERIAL_LANGUAGE : "en";
}

// What the backend gets as the document's language. It does not know the "en-US" code yet, so
// a US offer or PDF goes out as English, and the unit system travels in its own `unitSystem`
// field (point 2 of 5.7 in US-IMPERIAL-TERV.md). Drop this mapping once the backend has an
// en-US template.
export function toBackendLanguage(lng: string | undefined): string | undefined {
  return lng?.toLowerCase() === IMPERIAL_LANGUAGE.toLowerCase() ? "en" : lng;
}
